import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { LocalizarPedidoInputService } from 'src/app/core/services/behavior/localizar-pedido-input.service';
import { NavbarService } from 'src/app/core/services/behavior/navbar.service';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { CaixaService } from 'src/app/core/services/http/caixa.service';
import { ProdutoService } from 'src/app/core/services/http/produto.service';
import { SistemaService } from 'src/app/core/services/http/sistema.service';
import { TamanhoService } from 'src/app/core/services/http/tamanho.service';
import { AberturaCaixaDialogComponent, AberturaCaixaDialogDataClose } from 'src/app/shared/components/abertura-caixa-dialog/abertura-caixa-dialog.component';
import { ActiveTabCustomType, MudarActiveTabData } from 'src/app/shared/components/navbar/navbar.component';
import { ModalRoleEnum } from 'src/app/shared/enums/modal-role.enum';
import { ModalResult } from 'src/app/shared/interfaces/modal-result.interface';
import { DialogStateUtil } from 'src/app/shared/utils/dialog-state.util';
import { MesasComponent } from './mesas/mesas.component';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-home',
    templateUrl: './home.page.html',
    styleUrls: ['./home.page.scss'],
})
export class HomePage implements OnInit, OnDestroy {
    private subs$: Subscription[] = [];
    private readonly _logoutAposFechamentoStorageKey = 'logoutAposFechamentoObrigatorio';

    @ViewChild(MesasComponent) public mesasComponent: MesasComponent;

    public activeTab: ActiveTabCustomType = 'mesas';

    constructor(
        private _alertCtrl: AlertController,
        private _modalCtrl: ModalController,

        private _authService: AuthenticationService,
        private _caixaService: CaixaService,
        private produtoService: ProdutoService,
        private sistemaService: SistemaService,
        private storageService: StorageService,
        private tamanhoService: TamanhoService,
        private _localizarPedidoInputService: LocalizarPedidoInputService,
        private _navbarService: NavbarService,
    ) {

    }

    private get _activeTabBalcaoOuDelivery() {
        return this.activeTab === 'balcao' || this.activeTab === 'delivery';
    }

    public mudarActiveTab(activeTabData: MudarActiveTabData) {
        this._navbarService.numeroComandaDigitado$.next(null);

        this.activeTab = activeTabData.activeTab;

        if (this.activeTab !== 'mesas') {
            this._localizarPedidoInputService.novoValorParaSetar = activeTabData.numeroComanda.toString();
        }
    }

    public ngOnDestroy(): void {
        this.subs$.forEach(sub$ => sub$.unsubscribe());
    }

    public async ngOnInit() {
        const deveDeslogarAposFechamento = await this.storageService.get(this._logoutAposFechamentoStorageKey);

        if (deveDeslogarAposFechamento === true) {
            await this.storageService.remove(this._logoutAposFechamentoStorageKey);
            await this._authService.logout({
                removeDatabaseName: false,
                removeBaseUrl: false,
            });
            return;
        }

        const [produtosExist, familiaProdutosExist, tamanhosPizzaExist, tamanhosLasanhaExist, configuracoesExist] = await Promise.all([
            this.storageService.isExist('produtos'),
            this.storageService.isExist('familiaProdutos'),
            this.storageService.isExist('tamanhosPizza'),
            this.storageService.isExist('tamanhosLasanha'),
            this.storageService.isExist('configuracoes')
        ]);

        if (!produtosExist)
            await this.loadAndStore(() => this.produtoService.getProdutosDelivery(), 'produtos');

        if (!familiaProdutosExist)
            await this.loadAndStore(() => this.produtoService.getProdutosFamilia(), 'familiaProdutos');

        if (!tamanhosPizzaExist)
            await this.loadAndStore(() => this.tamanhoService.getTamanhosPizza(), 'tamanhosPizza');

        if (!tamanhosLasanhaExist)
            await this.loadAndStore(() => this.tamanhoService.getTamanhosLasanha(), 'tamanhosLasanha');

        if (!configuracoesExist)
            await this.loadAndStore(() => this.sistemaService.getConfiguracoes(), 'configuracoes');

        this._verificarStatusCaixa();

        this.solicitarSenhaSomentePrimeiraVez = await this.storageService.get('solicitarSenhaSomentePrimeiraVez') ?? true;
    }

    solicitarSenhaSomentePrimeiraVez: boolean = true;

    private _verificarStatusCaixa() {        
        const usuarioLogado = this._authService.user;
        
        if (!usuarioLogado.operadorCaixa) return;
        const sub1$ = this._caixaService
            .verificarCaixaAberto()
            .subscribe(async res => {                
                if (!res.success) return;

                if (!res.data) {
                    await this._exibirAlertaAsync('ATENÇÃO', 'Caixa está fechado! Realize a abertura.');

                    const dataClose = await this._abrirAberturaCaixaDialogAsync();

                    if (dataClose.data) {
                        this._caixaService.caixaAberto$.next(true);
                        return;
                    }
                }
                
                if (res?.data?.codUsuarioIni == usuarioLogado.codigo) {
                    this._caixaService.caixaAberto$.next(true);
                } else {                    
                    usuarioLogado.operadorCaixa = false;

                    await this.storageService.set(environment.credentialsKeys.user, usuarioLogado);
                }
            });

        this.subs$.push(sub1$);
    }


    private async _abrirAberturaCaixaDialogAsync() {
        const dialog = await this._modalCtrl.create({
            component: AberturaCaixaDialogComponent,
            cssClass: 'abertura-caixa-dialog',
            backdropDismiss: false,
            keyboardClose: false
        });

        await dialog.present();

        return await dialog.onWillDismiss() as ModalResult<AberturaCaixaDialogDataClose>;
    }

    public reloadMesas() {
        this.mesasComponent.reload();
    }

    public setActiveTab(tabName: ActiveTabCustomType) {
        this.activeTab = tabName;

        this._localizarPedidoInputService.limparValorParaSetar();

        this._navbarService.numeroComandaDigitado$.next(null);
    }

    @HostListener('window:keydown', ['$event'])
    public async onKeydown(event: KeyboardEvent): Promise<void> {
        const existeModalAberto = await this._modalCtrl.getTop();

        if (DialogStateUtil.isDialogCurrentlyOpen && existeModalAberto || existeModalAberto) {
            return;
        }

        const regex = /^[0-9]$/;

        if (regex.test(event.key) && this._localizarPedidoInputService.campoSenhaComFoco === false) {

            if (this._activeTabBalcaoOuDelivery && this._localizarPedidoInputService.estaFocado) {
                return;
            }

            this._navbarService.focarNumeroMesaInput$.next(true);
        } else if (event.key === 'F1') {
            this._localizarPedidoInputService.limparValorParaSetar();

            switch (this.activeTab) {
                case 'balcao':
                    this.activeTab = 'mesas';
                    break;
                case 'mesas':
                    this.activeTab = 'delivery';
                    break;
                case 'delivery':
                    this.activeTab = 'balcao';
                    break;
            }

            event.preventDefault();
            event.stopPropagation();
        } else if (event.key === 'F2') {

        } /* else if (event.key === 'Backspace' && !this._localizarPedidoInputService.estaFocado) {
       this._navbarService.focarNumeroMesaInput$.next(true);
    } */
    }

    private async _exibirAlertaAsync(title: string, msg: string) {
        const alert = await this._alertCtrl.create({
            header: title,
            message: msg,
            buttons: ['OK']
        });

        await alert.present();

        await alert.onWillDismiss();
    }

    private async loadAndStore(request: () => Observable<any>, key: string, fallback: any = []): Promise<void> {
        const res = await firstValueFrom(request());
        const data = res.success ? res.data : fallback;

        await this.storageService.set(key, data);
    }
}
